/**
 * IndexedDB Storage Service
 * Provides abstraction layer for storing large cached responses
 * Uses native IndexedDB for better compatibility and error handling
 */

class IndexedDBStore {
  constructor() {
    this.dbName = 'prepxai_cache';
    this.storeName = 'responses';
    this.db = null;
    this.initPromise = this.init();
  }

  /**
   * Initialize IndexedDB connection
   */
  async init() {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        console.error('IndexedDB initialization failed');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('expiresAt', 'expiresAt', { unique: false });
        }
      };
    });
  }

  /**
   * Store cached response
   */
  async set(key, data, options = {}) {
    const {
      ttl = 24 * 60 * 60 * 1000, // Default 24 hours
      apiVersion = '1.0',
    } = options;

    const db = await this.initPromise;
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    const cacheEntry = {
      key,
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
      apiVersion,
      etag: options.etag || null,
    };

    return new Promise((resolve, reject) => {
      const request = store.put(cacheEntry);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(cacheEntry);
    });
  }

  /**
   * Retrieve cached response
   */
  async get(key) {
    const db = await this.initPromise;
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;

        // Check if expired
        if (result && result.expiresAt < Date.now()) {
          this.delete(key); // Clean up expired entry
          resolve(null);
        } else {
          resolve(result);
        }
      };
    });
  }

  /**
   * Check if key exists and is not expired
   */
  async has(key) {
    const entry = await this.get(key);
    return entry !== null;
  }

  /**
   * Delete cached response
   */
  async delete(key) {
    const db = await this.initPromise;
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  /**
   * Clear all expired entries
   */
  async clearExpired() {
    const db = await this.initPromise;
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('expiresAt');

    return new Promise((resolve, reject) => {
      const range = IDBKeyRange.upperBound(Date.now());
      const request = index.getAll(range);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const results = request.result;
        const deletePromises = results.map((entry) => this.delete(entry.key));
        Promise.all(deletePromises).then(resolve).catch(reject);
      };
    });
  }

  /**
   * Clear all cache
   */
  async clear() {
    const db = await this.initPromise;
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  /**
   * Get cache size and stats
   */
  async getStats() {
    const db = await this.initPromise;
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const entries = request.result;
        const stats = {
          totalEntries: entries.length,
          totalSize: 0,
          expiredEntries: 0,
          entries: entries.map((e) => ({
            key: e.key,
            size: JSON.stringify(e).length,
            expires: new Date(e.expiresAt).toISOString(),
            isExpired: e.expiresAt < Date.now(),
          })),
        };

        stats.entries.forEach((e) => {
          stats.totalSize += e.size;
          if (e.isExpired) stats.expiredEntries += 1;
        });

        resolve(stats);
      };
    });
  }
}

// Singleton instance
let dbInstance = null;

export const getDB = () => {
  if (!dbInstance) {
    dbInstance = new IndexedDBStore();
  }
  return dbInstance;
};

export default IndexedDBStore;
